import { PaginationState } from 'app/shared/pagination/pagination.model';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  doActionForCase,
  fetchCaseModerators,
  fetchCases,
  saveAttachment,
  saveAttachments,
  saveCaseAdministrativeFee,
  saveCaseArchive,
  saveCaseAuthority,
  saveCaseExecutabilityValidity,
  saveCaseFinalActOtherInfo,
  saveCaseModerators,
  saveParticleArrangement,
  savePlannedDocumentation,
  validateTabsForAction,
} from 'app/edozvola/cases/service/cases.service';
import { useAppDispatch } from 'app/config/store';
import { setCase } from 'app/shared/reducers/case-entity';
import { showActValidationErrorToast, showToast } from 'app/shared/util/toast.util';
import { toast } from 'react-toastify';
import { ERRORS, messages } from 'app/config/constants';
import {
  initTabValidationErrors,
  setActValidationErrors,
  setTabValidationErrors,
  ValidationErrorState,
} from 'app/shared/reducers/validationerrors/validation-errors.reducer';
import {
  Attachment,
  AttachmentPage,
  CaseAdministrativeFeeModel,
  CaseArchiveModel,
  CaseAuthorityModel,
  CaseExecutabilityValidityModel,
  CaseFinalActOtherInfoModel,
  CaseModeratorsModel,
  ParticleArrangement,
  PlannedDocumentation,
} from 'app/edozvola/cases/cases.model';
import { queryClient } from 'app/app';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCaseContext } from 'app/shared/util/context-util';
import { LOCAL_STORAGE_KEYS } from 'app/shared/util/local-storage-keys';

export interface ICaseActionProps {
  idCase: string;
  caseActionKey: string;
  successMessage: string;
  errorMessage: string;
}

export const doActionForCaseQuery = closeConfirmationModal => {
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarItemPair } = getCaseContext();

  return useMutation({
    mutationFn: (caseActionProps: ICaseActionProps) => {
      return doActionForCase(caseActionProps.idCase, caseActionProps.caseActionKey).finally(closeConfirmationModal);
    },
    onSuccess: (data, variables, context) => {
      showToast(toast.success, variables.successMessage || messages.ACTION_SUCCESSFUL);

      if (variables.caseActionKey === 'PREDAJ_DOPUNU') {
        const amendmentCalledFrom = localStorage.getItem(LOCAL_STORAGE_KEYS.AMENDMENT_FROM) ? localStorage.getItem(LOCAL_STORAGE_KEYS.AMENDMENT_FROM) : location.state.from;
        setTimeout(() => localStorage.removeItem(LOCAL_STORAGE_KEYS.AMENDMENT_FROM))
        navigate(`/${amendmentCalledFrom}`, {replace: true});
      } else if (variables.caseActionKey === 'PREDAJ_ZAHTJEV_NEPRIJAVLJENI') {
        appDispatch(setCase(data));
        navigate(`/case-submission/case/${variables.idCase}`, { replace: true });
      } else if (data.caseStatus === 'Izbrisan') {
        navigate('/case-submission', {replace: true});
      } else {
        appDispatch(setCase(data));

        // if the current tab is not supported in next state redirect user to case starting page
        const result = data.caseTabs.flatMap(tab =>
          tab.sidebarItemPath ? [tab.sidebarItemPath] : tab.children?.map(child => child.sidebarItemPath),
        );

        if (!result.includes(sidebarItemPair?.sidebarItemPath)) {
          navigate(`/cases/${variables.idCase}`, {replace: true});
        }
      }
      appDispatch(initTabValidationErrors());
    },
    onError: (err: any, variables) => {
      showToast(toast.error,
        err.response.data.detail === ERRORS.ERROR_30_DAYS_PASSED_FROM_CREATING_CASE
          ? err.response.data.detail
          : variables.errorMessage || ERRORS.GENERIC_ERROR
      );
    },
  });
};

export const validateTabsForActionQuery = openConfirmationModal => {
  const appDispatch = useAppDispatch();

  return useMutation({
    mutationFn: (caseActionProps: ICaseActionProps) => {
      return validateTabsForAction(caseActionProps.idCase, caseActionProps.caseActionKey);
    },
    onSuccess: () => {
      openConfirmationModal();
    },
    onError: (err: any, variables) => {
      let showGenericError = true;
      if (
        err.response.data.parameters &&
        err.response.data.parameters.caseActsNotCreated &&
        err.response.data.parameters.caseActsNotCreated.length > 0
      ) {
        showGenericError = false;
        showActValidationErrorToast(err.response.data.parameters.caseActsNotCreated);
      }
      if (
        err.response.data.parameters &&
        err.response.data.parameters.caseActionValidationErrors &&
        err.response.data.parameters.caseActionValidationErrors.length > 0
      ) {
        appDispatch(
          setTabValidationErrors({
            idCase: variables.idCase,
            tabValidationErrors: err.response.data.parameters.tabValidationErrors,
          } as ValidationErrorState),
        );
        appDispatch(setActValidationErrors(err.response.data.parameters.actValidationErrors));
        showGenericError = false;
        err.response.data.parameters.caseActionValidationErrors.forEach(error => {
          showToast(toast.error, error);
        });
      } else if (showGenericError) {
        showToast(toast.error, variables.errorMessage || ERRORS.GENERIC_ERROR);
      }
    },
  });
};

export const fetchCasesQuery = (pagination: PaginationState) => {
  return useQuery({
    queryKey: ['cases', { ...pagination }],
    queryFn: ({ signal }) => fetchCases(pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const saveCaseModeratorsMutation = (caseId: string) => {
  return useMutation({
    mutationFn: (caseModerators: CaseModeratorsModel) => saveCaseModerators(caseId, caseModerators),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['case-moderators'] });
      showToast(toast.success, messages.DATA_SAVED);
    },
    onError: (err: any) => {
      if (Object.values(ERRORS).includes(err.response?.data?.detail)) {
        queryClient.invalidateQueries({queryKey: ['case-moderators']})
        showToast(toast.error, err.response?.data?.detail);
      } else {
        showToast(toast.error, ERRORS.GENERIC_ERROR);
      }
    },
  });
};

export const fetchCaseModeratorsQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['case-moderators'],
    queryFn: ({ signal }) => fetchCaseModerators(idCase),
    enabled: !!idCase,
  });
};

export const updateCaseAdministrativeFeeMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: CaseAdministrativeFeeModel) => saveCaseAdministrativeFee(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseArchiveMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: CaseArchiveModel) => saveCaseArchive(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseAuthorityMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: CaseAuthorityModel) => saveCaseAuthority(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseExecutabilityValidityMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: CaseExecutabilityValidityModel) => saveCaseExecutabilityValidity(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseFinalActOtherInfoMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: CaseFinalActOtherInfoModel) => saveCaseFinalActOtherInfo(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseParticleArrangementMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: ParticleArrangement) => saveParticleArrangement(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCasePlannedDocumentationMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: PlannedDocumentation) => savePlannedDocumentation(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseRequestAttachmentsMutation = (uuid: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: AttachmentPage) => saveAttachments(uuid, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};

export const updateCaseRequestAttachmentMutation = (uuid: string, idAttachment: string, onSuccess?: Function, onError?: Function) => {
  return useMutation({
    mutationFn: (data: Attachment) => saveAttachment(uuid, idAttachment, data),
    onSuccess: data => {
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error: any) => {
      if (onError) {
        onError(error);
      }
    }
  });
};


